import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from "react"

// DateTime
import { setLang as setD3Lang } from "@/lib/charts"
import { DateTime, setLocale as setDateLocale } from "@/lib/dates"

// Translations
import { useTranslation } from "@/lib/i18n"

// State
import { useSelector, useDispatch } from "@/state/StateProvider"
import {
	UserPreferencesState,
	setLang,
} from "@/state/features/userPreferencesSlice"

// Types
import { UsersUserProfileLanguageChoices } from "@/api/graphql/types"
type FormatNumberType = (
	number: number | bigint,
	options?: Intl.NumberFormatOptions | undefined,
) => string
type FormatCurrencyType = (
	number: number | bigint,
	options?: Intl.NumberFormatOptions | undefined,
) => string
interface LangContextType {
	lang: UsersUserProfileLanguageChoices
	formatNumber: FormatNumberType
	formatCurrency: FormatCurrencyType
	formatDate: (
		date: DateTime,
		options?: { variant?: "pretty" | "verbose" | "computer" },
	) => string
	setLang: (newLang: UsersUserProfileLanguageChoices) => void
}

const LangContext = createContext<LangContextType>(null!)

interface LanguageProviderProps {
	lang?: UsersUserProfileLanguageChoices
	children: ReactNode | ((value: LangContextType) => ReactNode)
}

/**
 * LanguageProvider
 * @param param0
 * @returns
 */
export const LanguageProvider = ({ children }: LanguageProviderProps) => {
	const { i18n } = useTranslation()
	const dispatch = useDispatch()

	// State
	const lang = useSelector(
		({ userPreferences }: { userPreferences: UserPreferencesState }) =>
			userPreferences.lang,
	)

	// On mount, configure all langs
	useEffect(() => {
		void setLangProxy(lang)
	}, [])

	// Format number according to selected lang
	const formatNumber: FormatNumberType = useMemo(
		() => (number, options) => {
			const customFormatter = new Intl.NumberFormat(lang, options)
			return customFormatter.format(number)
		},
		[lang], // Update when lang is changed
	)

	// Format currency with default EUR in NL style, but allow overrides
	const formatCurrency: FormatCurrencyType = useMemo(
		() => (number, options) => {
			const defaultOptions = {
				style: "currency" as const,
				currency: "EUR",
			}
			const mergedOptions = { ...defaultOptions, ...options }
			const formatter = new Intl.NumberFormat(lang, mergedOptions)
			return formatter.format(number)
		},
		[lang], // Will update when lang is changed
	)

	// Execute all these functions when the locale is changed
	const setLangProxy = useCallback(
		async (lang: UsersUserProfileLanguageChoices) => {
			const lowerCaseLang = lang.toLowerCase()
			setDateLocale(lowerCaseLang) // Update Luxon DateTime
			void setD3Lang(lowerCaseLang) // Update D3. TODO: Find out if this can be removed
			void i18n.changeLanguage(lowerCaseLang) // Update i18n
			dispatch(setLang(lang)) // Save in Redux
		},
		[i18n],
	)

	// Format date according to lang set
	const formatDate: LangContextType["formatDate"] = useMemo(
		() =>
			(date: DateTime, { variant } = {}) => {
				if (variant === "pretty") {
					return date.toLocaleString(
						{
							weekday: "long",
							month: "long",
							day: "2-digit",
							year: "2-digit",
						},
						{
							locale: lang,
						},
					)
				}

				// Looks like: "Sunday, 29 October 2023"
				if (variant === "verbose") {
					return date.toLocaleString(
						{
							weekday: "long",
							month: "long",
							day: "2-digit",
							year: "numeric",
						},
						{
							locale: lang,
						},
					)
				}

				// Default format for "computer" variant or no variant
				return date.toLocaleString(
					{
						year: "numeric",
						month: "2-digit",
						day: "2-digit",
					},
					{
						locale: lang,
					},
				)
			},
		[lang], // Update when lang changes
	)

	const value = useMemo(
		() => ({
			lang,
			setLang: setLangProxy,
			formatNumber: formatNumber,
			formatCurrency,
			formatDate,
		}),
		[formatCurrency, formatDate, lang, formatNumber, setLangProxy],
	)

	return (
		<LangContext.Provider value={value}>
			{typeof children === "function" ? children(value) : children}
		</LangContext.Provider>
	)
}

export const useLang = () => useContext(LangContext)
